import { Box, CircularProgress } from "@mui/material";
import React, { useEffect, useReducer, useState } from "react";
import { toast } from "react-toastify";
import AddModal from "../../components/add-modal";
import ClientList from "../../components/client-list";
import DashboardContainer from "../../components/dashboard-container";
import CustomPagination from "../../components/pagination";
import { isMobile } from "../../globalStyles/mobile";
import { clientService } from "../../utils/services/clientService";
import ManageClient from "../../components/create-client";
import { clientFilterInitialState } from "../../utils/filter-config";
import buildQuery from "../../utils/query-builder";

const initialData = {
  _id: null,
  name: "",
  country: "",
  contactCode: "",
  email: "",
  contactNumber: "",
  businessName: "",
  managed_orchestration: false,
};

function filterReducer(state, action) {
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        columns: state?.columns.map((x) =>
          x?.key === action?.payload?.key ? { ...x, ...action.payload } : x
        ),
      };
    case "SET_OPTIONS":
      return {
        ...state,
        columns: state.columns.map((col) =>
          col.key === action.payload.key
            ? { ...col, options: action.payload.options }
            : col
        ),
      };
    case "UPDATE_LIMIT":
      return { ...state, limit: action.payload };
    case "EXISTING_DATA":
      return {
        ...state,
        columns: state.columns.map((x) => ({
          ...x,
          filter:
            action.data.filters.find((y) => y.key === x.key)?.values ||
            x.filter,
        })),
        limit: action.data.limit || state.limit,
      };

    default:
      return state;
  }
}

export default function Client() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState(initialData);
  const [clientData, setClientData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [dataCount, setDataCount] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [filterData, dispatch] = useReducer(
    filterReducer,
    clientFilterInitialState
  );

  function phonenumber(inputtxt) {
    const phoneno = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    return phoneno.test(inputtxt);
  }

  function isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  const validateForm = () => {
    const errors = {};

    if (!formData.name) {
      errors.name = "Client name is required";
    }

    if (!formData.businessName) {
      errors.businessName = "Client Business Name is required";
    }

    if (!phonenumber(formData.contactNumber)) {
      errors.mobileNumber = "Enter a valid number";
    }

    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!isValidEmail(formData.email)) {
      errors.email = "Enter a valid email address";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const InitialData = () => {
    setFormData(initialData);
    setSelectedClient(null);
    setLoading(false);
  };

  const fetchClientData = async () => {
    setLoading(true);
    try {
      const response = await clientService.list(
        buildQuery({ ...filterData, limit: rowsPerPage }, page)
      );
      setClientData(response.data);
      setDataCount(response.total);
    } catch (error) {
      console.error("Fetch failed:", error);
      // Handle fetch failure
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientData();
  }, [filterData, rowsPerPage, page]);

  useEffect(() => {
    if (selectedClient) {
      const contactParts = selectedClient?.contact?.split("-");
      const contactCode = contactParts?.length > 1 ? contactParts[0] : "";
      const contactNumber = contactParts?.length > 1 ? contactParts[1] : "";
      setFormData({
        _id: selectedClient?._id,
        name: selectedClient?.name,
        country: selectedClient?.country || "",
        email: selectedClient?.email,
        contactCode: contactCode || "",
        contactNumber: contactNumber || "",
        businessName: selectedClient?.business_name,
        managed_orchestration: selectedClient?.managed_orchestration || false,
      });
    }
  }, [selectedClient]);

  const handleInputChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const createClient = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const contact = `${formData.contactCode}-${formData.contactNumber}`;
      if (selectedClient) {
        await clientService.edit(
          {
            name: formData.name,
            email: formData.email,
            contact,
            country: formData.country,
            business_name: formData.businessName,
            managed_orchestration: formData.managed_orchestration,
          },
          selectedClient._id
        );
        toast.success("Client Updated successfully!");
      } else {
        await clientService.create({
          name: formData.name,
          email: formData.email,
          contact,
          country: formData.country,
          business_name: formData.businessName,
          managed_orchestration: formData.managed_orchestration,
        });
        toast.success("Client Created successfully!");
      }
      fetchClientData();
      InitialData();
      setShowAddModal(false);
    } catch (error) {
      console.error("Client create/edit failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const deleteClient = async (e) => {
    try {
      setLoading(true);
      await clientService.delete(e._id);
      fetchClientData();
      InitialData();
    } catch (error) {
      console.error("Delete failed:", error);
      InitialData();
    }
  };

  const NoDataImage = () => (
    <div style={{ width: "100%", justifyContent: "center", display: "flex" }}>
      <img
        src="no_data_client.png"
        alt="No data available"
        style={{ objectFit: "cover", height: "340px" }}
      />
    </div>
  );

  const handleFilterChange = (newFilter) => {
    dispatch({ type: "SET_FILTER", payload: newFilter });
    setPage(0);
  };

  return (
    <DashboardContainer
      searchBarPlaceholder={"Search by Business Name"}
      onSearchData={(x) => {
        setSearchValue(x.trim() ? x : "");
        setPage(0);
      }}
      onClickAddButton={() => {
        setShowAddModal(true);
        setFormErrors({});
      }}
      hideUploadButton={true}
      header={"Clients"}
      hideFilterClient={true}
      setViewMode={setViewMode}
      title={`${isMobile() ? "+ Add" : "+ Add Client"}`}
      client
      onClickRefresh={fetchClientData}
      filterButton={false}
    >
      <ManageClient
        formData={formData}
        onChangeData={(col, e) => handleInputChange(col, e)}
        client
        title={"Client"}
        onClickAdd={createClient}
        handleClose={() => {
          InitialData();
          setShowAddModal(false);
        }}
        open={showAddModal}
        formErrors={formErrors}
      ></ManageClient>
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            color: "white",
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      )}
      {clientData.length === 0 ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <NoDataImage />
        </div>
      ) : (
        <>
          <ClientList
            onSuccess={fetchClientData}
            onChangeStatus={fetchClientData}
            handleDeleteClient={deleteClient}
            client
            onDeleteData={fetchClientData}
            handleEditClient={(e) => {
              setShowAddModal(true);
              setSelectedClient(e);
              setFormErrors({});
            }}
            data={clientData}
            viewMode={viewMode}
            currentPage={page}
            itemsPerPage={rowsPerPage}
            filterData={filterData}
            onChangeFilter={handleFilterChange}
          />
        </>
      )}
      {/* <TablePagination
        slotProps={{
          actions: {
            nextButton: {
              disabled: dataCount <= rowsPerPage * (page + 1),
            },
          },
        }}
        nextIconButton={
          <IconButton>
            <KeyboardArrowRight />
          </IconButton>
        }
        backIconButton={
          <IconButton>
            <KeyboardArrowLeft />
          </IconButton>
        }
        rowsPerPageOptions={[15, 25, 45, 75, 100]}
        component="div"
        count={dataCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "#ffffff",
          padding: "2px",
          borderTop: "1px solid #dddddd",
        }}
      /> */}
      <CustomPagination
        dataCount={dataCount}
        rowsPerPage={rowsPerPage}
        page={page}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        disableNext={dataCount <= rowsPerPage * (page + 1)}
      />
    </DashboardContainer>
  );
}
